import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';

export interface WordCloudWord {
  text: string;
  size: number;
  id?: string;
  count?: number;
}

export interface WordCloudProps {
  words: WordCloudWord[];
  colors?: string[];
  maxFontSize?: number;
  minFontSize?: number;
  width?: number;
  height?: number;
  className?: string;
  onWordClick?: (word: WordCloudWord) => void;
  selectedWords?: string[];
  responsive?: boolean;
}

interface PositionedWord extends WordCloudWord {
  x: number;
  y: number;
  fontSize: number;
  color: string;
  rotation: number;
  zIndex: number;
}

// Reduced, more subtle color palette
const DEFAULT_COLORS = [
  '#1e40af', // Blue
  '#dc2626', // Red  
  '#059669', // Green
  '#7c3aed', // Purple
  '#ea580c', // Orange
  '#0891b2', // Cyan
];

export function WordCloud({
  words,
  colors = DEFAULT_COLORS,
  maxFontSize = 56,
  minFontSize = 16,
  width,
  height = 600,
  className = '',
  onWordClick,
  selectedWords = [],
  responsive = true,
}: WordCloudProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [hoveredWord, setHoveredWord] = useState<string | null>(null);
  const [positionedWords, setPositionedWords] = useState<PositionedWord[]>([]);
  const [animationPhase, setAnimationPhase] = useState(0);
  const [dimensions, setDimensions] = useState({ width: width || 800, height });

  // Handle responsive sizing
  useEffect(() => {
    if (!responsive || width) return;

    const updateDimensions = () => {
      if (containerRef.current) {
        const containerWidth = containerRef.current.offsetWidth;
        setDimensions({
          width: Math.max(containerWidth, 600),
          height: height
        });
      }
    };

    updateDimensions();
    window.addEventListener('resize', updateDimensions);
    return () => window.removeEventListener('resize', updateDimensions);
  }, [responsive, width, height]);

  // Memoize colors to prevent recreation
  const memoizedColors = useMemo(() => colors, [colors.join(',')]);

  // Calculate font sizes and process words
  const processedWords = useMemo(() => {
    if (!words.length) return [];

    // Find min and max sizes for scaling
    const sizes = words.map(w => w.size);
    const minSize = Math.min(...sizes);
    const maxSize = Math.max(...sizes);
    const sizeRange = maxSize - minSize || 1;

    return words.map((word, index) => {
      // Scale font size with better curve
      const normalizedSize = (word.size - minSize) / sizeRange;
      const curve = Math.pow(normalizedSize, 0.7); // Slightly less aggressive scaling
      const fontSize = minFontSize + (curve * (maxFontSize - minFontSize));
      
      // Assign color with better distribution
      const colorIndex = word.text.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
      const color = memoizedColors[colorIndex % memoizedColors.length];
      
      // Reduced rotation for better readability
      const seed = word.text.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
      const rotations = [-8, -5, 0, 0, 0, 5, 8]; // Mostly 0 rotation, smaller angles
      const rotation = rotations[seed % rotations.length];
      
      return {
        ...word,
        fontSize,
        color,
        rotation,
        zIndex: Math.floor(fontSize),
        x: 0,
        y: 0,
      };
    });
  }, [words, memoizedColors, maxFontSize, minFontSize]);

  // Enhanced positioning algorithm with better aesthetics
  useEffect(() => {
    if (!processedWords.length) {
      setPositionedWords([]);
      return;
    }

    const positioned: PositionedWord[] = [];
    const centerX = dimensions.width / 2;
    const centerY = dimensions.height / 2;
    const padding = 12;

    // Sort words by size and importance
    const sortedWords = [...processedWords].sort((a, b) => {
      const aImportance = a.fontSize + (a.count || 0) * 2;
      const bImportance = b.fontSize + (b.count || 0) * 2;
      return bImportance - aImportance;
    });

    sortedWords.forEach((word, index) => {
      let placed = false;
      let attempts = 0;
      const maxAttempts = 200;
      
      // Estimate word dimensions more accurately
      const avgCharWidth = word.fontSize * 0.55;
      const wordWidth = word.text.length * avgCharWidth;
      const wordHeight = word.fontSize * 1.2;
      
      while (!placed && attempts < maxAttempts) {
        let x, y;
        
        if (index === 0) {
          // Place largest word in center
          x = centerX;
          y = centerY;
        } else if (index < 3) {
          // Place next important words in a circle around center
          const angle = (index - 1) * (Math.PI * 2 / 6) + Math.PI / 4;
          const radius = 80 + attempts * 5;
          x = centerX + Math.cos(angle) * radius;
          y = centerY + Math.sin(angle) * radius;
        } else {
          // Use improved spiral placement
          const goldenAngle = Math.PI * (3 - Math.sqrt(5)); // Golden angle
          const angle = index * goldenAngle + attempts * 0.3;
          const radius = Math.sqrt(index) * 25 + attempts * 8;
          x = centerX + Math.cos(angle) * radius;
          y = centerY + Math.sin(angle) * radius;
          
          // Add some randomness for natural look
          x += (Math.random() - 0.5) * 20;
          y += (Math.random() - 0.5) * 20;
        }

        // Check bounds with padding
        const margin = 20;
        if (x - wordWidth/2 >= margin && 
            x + wordWidth/2 <= dimensions.width - margin && 
            y - wordHeight/2 >= margin && 
            y + wordHeight/2 <= dimensions.height - margin) {
          
          // Check collision with existing words
          const hasCollision = positioned.some(existing => {
            const existingWidth = existing.text.length * existing.fontSize * 0.55;
            const existingHeight = existing.fontSize * 1.2;
            
            const minDistance = Math.max(
              (wordWidth + existingWidth) / 2 + padding,
              (wordHeight + existingHeight) / 2 + padding
            );
            
            const distance = Math.sqrt(
              Math.pow(x - existing.x, 2) + Math.pow(y - existing.y, 2)
            );
            
            return distance < minDistance;
          });

          if (!hasCollision) {
            positioned.push({ ...word, x, y });
            placed = true;
          }
        }
        
        attempts++;
      }

      // Fallback placement if we couldn't find a good spot
      if (!placed && positioned.length < 50) { // Limit fallback to prevent overcrowding
        const x = centerX + (Math.random() - 0.5) * dimensions.width * 0.8;
        const y = centerY + (Math.random() - 0.5) * dimensions.height * 0.8;
        positioned.push({ ...word, x, y });
      }
    });

    setPositionedWords(positioned);
  }, [processedWords, dimensions]);

  // Reduced animation effect
  useEffect(() => {
    const interval = setInterval(() => {
      setAnimationPhase(prev => (prev + 1) % 100);
    }, 200); // Slower animation
    return () => clearInterval(interval);
  }, []);

  const handleWordClick = useCallback((word: PositionedWord) => {
    if (onWordClick) {
      onWordClick(word);
    }
  }, [onWordClick]);

  const handleWordMouseEnter = useCallback((word: PositionedWord) => {
    setHoveredWord(word.text);
  }, []);

  const handleWordMouseLeave = useCallback(() => {
    setHoveredWord(null);
  }, []);

  if (!words.length) {
    return (
      <div 
        ref={containerRef}
        className={`flex items-center justify-center comic-panel bg-gradient-to-br from-yellow-200 to-blue-200 ${className}`} 
        style={{ width: responsive ? '100%' : dimensions.width, height: dimensions.height }}
      >
        <div className="text-center pop-art-text">
          <div className="text-6xl mb-4">🏷️</div>
          <h3 className="text-pop-bold text-2xl mb-4">NO WORDS TO DISPLAY!</h3>
          <p className="text-lg font-semibold">Add some content to see the amazing word cloud</p>
        </div>
      </div>
    );
  }

  return (
    <div 
      ref={containerRef}
      className={`relative overflow-hidden comic-panel bg-gradient-to-br from-white via-yellow-50 to-blue-50 ${className}`}
      style={{ width: responsive ? '100%' : dimensions.width, height: dimensions.height }}
      role="img"
      aria-label="Interactive tag cloud visualization"
    >
      {/* Subtle animated background patterns */}
      <div className="absolute inset-0 opacity-5">
        <div 
          className="absolute w-full h-full bg-ben-day-light"
          style={{
            transform: `translateX(${Math.sin(animationPhase * 0.05) * 5}px) translateY(${Math.cos(animationPhase * 0.025) * 3}px)`
          }}
        />
      </div>

      <svg 
        width={dimensions.width} 
        height={dimensions.height} 
        className="absolute inset-0 z-10"
        viewBox={`0 0 ${dimensions.width} ${dimensions.height}`}
        preserveAspectRatio="xMidYMid meet"
      >
        {/* Subtle shadow filter */}
        <defs>
          <filter id="subtle-shadow">
            <feDropShadow dx="1" dy="1" stdDeviation="1" floodColor="rgba(0,0,0,0.2)"/>
          </filter>
        </defs>

        {positionedWords
          .sort((a, b) => a.zIndex - b.zIndex) // Render smaller words first
          .map((word, index) => {
            const isSelected = selectedWords.includes(word.id || word.text);
            const isHovered = hoveredWord === word.text;
            const isImportant = word.fontSize > maxFontSize * 0.7;
            
            // Reduced animation effects - minimal scale and no movement
            const scale = isHovered ? 1.001 : (isSelected ? 1.03 : 1);
            const opacity = isSelected ? 1 : (isHovered ? 0.8 : 0.7);
            
            // Very subtle floating animation - much reduced
            const floatY = Math.sin((animationPhase + index * 5) * 0.05) * 0.5;
            
            return (
              <g key={`${word.text}-${index}`}>
                {/* Main word with subtle shadow */}
                <text
                  x={word.x}
                  y={word.y + floatY}
                  fontSize={word.fontSize}
                  fill={isSelected ? '#1e293b' : word.color}
                  textAnchor="middle"
                  dominantBaseline="middle"
                  transform={`rotate(${word.rotation} ${word.x} ${word.y}) scale(${scale})`}
                  style={{
                    cursor: onWordClick ? 'pointer' : 'default',
                    transition: 'all 0.2s ease-out',
                    opacity,
                    fontFamily: '"Fira Sans", "Inter", system-ui, sans-serif',
                    fontWeight: isSelected ? 800 : (isImportant ? 'bold' : 600),
                    filter: 'url(#subtle-shadow)',
                  }}
                  onClick={() => handleWordClick(word)}
                  onMouseEnter={() => handleWordMouseEnter(word)}
                  onMouseLeave={handleWordMouseLeave}
                  role={onWordClick ? 'button' : undefined}
                  aria-label={`${word.text}${word.count ? ` (${word.count} references)` : ''}`}
                  tabIndex={onWordClick ? 0 : undefined}
                  onKeyDown={(e) => {
                    if (onWordClick && (e.key === 'Enter' || e.key === ' ')) {
                      e.preventDefault();
                      handleWordClick(word);
                    }
                  }}
                >
                  {word.text}
                </text>
                
                {/* Interactive tooltip */}
                {isHovered && word.count && (
                  <g>
                    {/* Tooltip background with subtle styling */}
                    <rect
                      x={word.x - 35}
                      y={word.y - word.fontSize - 30}
                      width={70}
                      height={20}
                      fill="rgba(30, 41, 59, 0.9)"
                      stroke="rgba(0,0,0,0.1)"
                      strokeWidth="1"
                      rx={3}
                    />
                    {/* Tooltip text */}
                    <text
                      x={word.x}
                      y={word.y - word.fontSize - 18}
                      fontSize={11}
                      fill="white"
                      textAnchor="middle"
                      dominantBaseline="middle"
                      style={{
                        fontFamily: '"Fira Sans", "Inter", system-ui, sans-serif',
                        fontWeight: '600',
                        pointerEvents: 'none',
                      }}
                    >
                      {word.count} ref{word.count !== 1 ? 's' : ''}
                    </text>
                  </g>
                )}
              </g>
            );
          })}
      </svg>
      
      {/* Simplified legend */}
      <div className="absolute top-4 right-4 comic-panel bg-white/90 p-3 rounded-lg shadow-lg border border-gray-200 z-20">
        <h4 className="text-pop-bold text-sm mb-2 text-gray-800">WORD CLOUD</h4>
        <div className="space-y-1 text-xs">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
            <span className="font-semibold">{words.length} tags</span>
          </div>
          {onWordClick && (
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-green-500 rounded-full"></div>
              <span className="font-semibold">Click to filter</span>
            </div>
          )}
        </div>
      </div>
      
      {/* Non-button-like interaction hint */}
      {onWordClick && !hoveredWord && (
        <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 z-20">
          <div className="bg-gray-700/80 text-white px-3 py-1 rounded text-sm font-medium">
            💡 Hover and click tags to explore
          </div>
        </div>
      )}
    </div>
  );
} 